import { useTranslations } from '@vocab/react';
import { Box, Notice, Stack, Text } from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormContext } from 'react-hook-form';

import { useSkills } from 'src/components/Skills/hooks/useSkills';
import { useConfig } from 'src/context/config';
import { StepId, useStepsCompleted } from 'src/context/use-steps-completed';
import {
  CareerHistory,
  useCareerHistoryData,
} from 'src/pages/Profile/components/CareerHistory';
import { useTracking } from 'src/pages/Profile/hooks/useTracking';
import { AlertMessage } from 'src/ui/AlertMessage/AlertMessage';
import { BottomNav } from 'src/ui/BottomNav/BottomNav';

import { REFERENCES_NAMESPACE } from '../../components/References/ReferencesList.tsx';
import { ScrollOnMount } from '../../ui/ScrollOnMount/ScrollOnMount.tsx';

import translations from './.vocab';
import Education from './components/Education/Education';
import { Licences } from './components/Licences/Licences';
import { References } from './components/References/References.tsx';
import Skills from './components/Skills/Skills';
import { useUnconfirmedData } from './hooks/useUnconfirmedData';

interface ProfileProps {
  resumeId?: string;
  onNext?: () => void;
  onPrev?: () => void;
  isNewToWorkforce: boolean;
  onNewToWorkforceToggle: (toggle: boolean) => void;
}

export const Profile = ({
  onNext,
  onPrev,
  isNewToWorkforce,
  onNewToWorkforceToggle,
}: ProfileProps) => {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const alertRef = useRef<HTMLElement>(null);

  // This is here to listen to roles changes.
  // refactor later to a fragment and use it in CareerHistory.tsx
  const { roles } = useCareerHistoryData();
  const { skills } = useSkills();
  const unconfirmedData = useUnconfirmedData({});
  const { setCompleted } = useStepsCompleted();
  const [showError, setShowError] = useState(false);
  const isValid = roles.length > 0 ? true : isNewToWorkforce;

  const { handleSubmit, formState } = useFormContext();

  const validationErrors = Object.entries(
    formState.errors[REFERENCES_NAMESPACE] || {},
  );

  const getValidationMessage = () => {
    const fieldName = t('References');
    const includedError = validationErrors.find(([key]) => key === 'selection');
    return includedError ? { [fieldName]: includedError[1]?.message } : '';
  };

  useTracking({
    loading: unconfirmedData.loading,
    skillCount: skills.length,
    unconfirmedRoleCount: unconfirmedData.roles.length,
    unconfirmedQualificationCount: unconfirmedData.qualifications.length,
  });

  const scrollToAlert = () => {
    alertRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isValid) {
      setShowError(false);
    }
  }, [isValid]);

  const handlePrevious = () => {
    if (!isValid) {
      setShowError(true);
      scrollToAlert();
      return;
    }

    onPrev?.();
  };

  const handleNext = () => {
    if (!isValid) {
      setShowError(true);
      scrollToAlert();
      return;
    }

    setCompleted(StepId.PROFILE_ID);
    onNext?.();
  };

  return (
    <Stack space="xxlarge">
      <Helmet>
        <title>{`${t('Update SEEK Profile', { brand: config.BRAND })} | ${
          config.BRAND
        }`}</title>
      </Helmet>

      {validationErrors.length > 0 && (
        <ScrollOnMount key={formState.submitCount}>
          <AlertMessage message={getValidationMessage()} />
        </ScrollOnMount>
      )}

      {showError && (
        <Box ref={alertRef}>
          <AlertMessage
            message={{ [t('Career History')]: t('Please update your') }}
          />
        </Box>
      )}

      <Notice tone="info">
        <Text>{t('Your SEEK Profile', { brand: config.BRAND })}</Text>
      </Notice>

      <CareerHistory
        unconfirmedRoles={unconfirmedData.roles}
        isNewToWorkforce={isNewToWorkforce}
        onNewToWorkforceToggle={onNewToWorkforceToggle}
      />
      <Education unconfirmedQualifications={unconfirmedData.qualifications} />
      <Licences />

      <Skills />

      <References />

      <BottomNav
        onPrevious={handleSubmit(handlePrevious)}
        onNext={handleSubmit(handleNext)}
      />
    </Stack>
  );
};
